<script setup>
import { h, ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRouter } from 'vue-router';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { useConfig } from 'dashboard/composables/useConfig';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useFocusMode } from 'dashboard/composables/useFocusMode';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { resolveSavedIconClass } from 'dashboard/helper/legacyIconClass';
import { useWindowSize, useEventListener } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarTasksBell from './SidebarTasksBell.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute } = useAccount();
const router = useRouter();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();
const { isStacklab } = useConfig();
const { uiSettings } = useUISettings();
// SiteUp — Modo Foco: o mesmo botão liga e desliga. A nav fica em rail;
// não some atrás de um dock roxo.
const { isFocusModeEnabled, toggleFocusMode } = useFocusMode();

const onFocusModeClick = () => {
  const firstActivation = toggleFocusMode();
  if (firstActivation) {
    router.replace(accountScopedRoute('focus_hub'));
  }
};

const isRTL = useMapGetter('accounts/isRTL');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const hasAdvancedAssignment = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ADVANCED_ASSIGNMENT
  );
});

const hasConversationUnreadCounts = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS
  );
});

const fetchConversationUnreadCounts = ([currentAccountId, isEnabled]) => {
  if (!currentAccountId) return;

  if (!isEnabled) {
    store.dispatch('conversationUnreadCounts/clear');
    return;
  }

  store.dispatch('conversationUnreadCounts/get');
};

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const toggleCollapse = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const labels = useMapGetter('labels/getLabelsOnSidebar');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const dashboardApps = useMapGetter('dashboardApps/getNavigationApps');
const funnels = useMapGetter('funnel/getFunnels');
const kanbanItems = useMapGetter('kanban/getKanbanItems');
const selectedFunnel = useMapGetter('funnel/getSelectedFunnel');

let teamChatPollInterval = null;

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
  store.dispatch('dashboardApps/get');
  store.dispatch('funnel/fetch');
  store.dispatch('kanban/fetchKanbanConfig');

  // Team chat unread count — initial fetch + poll every 30s so the sidebar
  // badge stays fresh without a dedicated ActionCable subscription. The team
  // chat page itself uses live events so this is only relevant when the user
  // is NOT currently on the team chat route.
  store.dispatch('teamChat/fetchUnreadCount');
  teamChatPollInterval = setInterval(() => {
    store.dispatch('teamChat/fetchUnreadCount');
  }, 30000);
});

onBeforeUnmount(() => {
  if (teamChatPollInterval) {
    clearInterval(teamChatPollInterval);
    teamChatPollInterval = null;
  }
});

watch([accountId, hasConversationUnreadCounts], fetchConversationUnreadCounts, {
  immediate: true,
});

const recentKanbanItems = computed(() => {
  if (!selectedFunnel.value) return [];

  return (
    kanbanItems.value
      ?.slice()
      .sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0);
        const dateB = new Date(b.updated_at || b.created_at || 0);
        return dateB - dateA;
      })
      .slice(0, 5) || []
  );
});
const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const newReportRoutes = () => [
  {
    name: 'Reports Kanban',
    label: t('SIDEBAR.REPORTS_KANBAN'),
    to: accountScopedRoute('kanban_reports'),
  },
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
  {
    name: 'Reports Integration Events',
    label: 'Conversoes enviadas',
    to: accountScopedRoute('integration_events_report'),
  },
];

const reportRoutes = computed(() => newReportRoutes());

const menuItems = computed(() => {
  const items = [
    {
      name: 'Inbox',
      label: t('SIDEBAR.INBOX'),
      icon: 'i-ri-inbox-line',
      to: accountScopedRoute('inbox_view'),
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      getterKeys: {
        count: 'notifications/getUnreadCount',
      },
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-ri-chat-1-line',
      // SiteUp: "Conversas" é um link direto pra lista principal. O submenu
      // (Todas/Não lidas/Pastas/Times/Caixas/Etiquetas) foi removido por ser
      // redundante com a barra de filtros rápidos (chips configuráveis).
      to: accountScopedRoute('home'),
      activeOn: [
        'inbox_conversation',
        'conversation_through_inbox',
        'conversation_through_unread',
        'conversations_through_folders',
        'conversations_through_team',
        'conversations_through_label',
      ],
    },
    {
      name: 'InternalChat',
      label: t('SIDEBAR.INTERNAL_CHAT'),
      icon: 'i-ri-chat-3-line',
      to: accountScopedRoute('internal_chat'),
      activeOn: ['internal_chat'],
    },
    {
      name: 'Kanban',
      label: t('SIDEBAR.KANBAN_BOARD_TITLE', {
        title: store.getters['kanban/getKanbanTitle'],
      }),
      icon: 'i-ri-layout-grid-line',
      featureFlag: 'kanban_board',
      children: [
        {
          name: 'Stages',
          label: 'Etapas',
          icon: h(
            'svg',
            {
              xmlns: 'http://www.w3.org/2000/svg',
              width: '16',
              height: '16',
              viewBox: '0 0 24 24',
              fill: 'none',
              stroke: 'currentColor',
              'stroke-width': '2',
              'stroke-linecap': 'round',
              'stroke-linejoin': 'round',
              class: 'lucide lucide-kanban-icon lucide-kanban',
            },
            [
              h('path', { d: 'M5 3v14' }),
              h('path', { d: 'M12 3v8' }),
              h('path', { d: 'M19 3v18' }),
            ]
          ),
          inactiveOnQuery: { view: 'scheduled_messages' },
          activeOn: ['kanban_board'],
          to: accountScopedRoute('kanban_board'),
        },
        {
          name: 'Agenda',
          label: 'Agenda',
          icon: 'i-ri-calendar-2-line',
          activeOn: ['kanban_board'],
          to: accountScopedRoute('kanban_board', {}, { view: 'agenda' }),
        },
        {
          name: 'Scheduled Messages',
          label: t('SCHEDULE_MESSAGES.LIST.TITLE'),
          icon: 'i-ri-time-line',
          activeOn: ['kanban_board'],
          to: accountScopedRoute(
            'kanban_board',
            {},
            { view: 'scheduled_messages' }
          ),
        },
        ...(isStacklab
          ? [
              {
                name: 'Offers',
                label: t('SIDEBAR.KANBAN_OFFERS_TITLE', {
                  title:
                    store.getters['kanban/getOffersTabTitle'] ||
                    t('KANBAN.TABS.OFFERS'),
                }),
                icon: 'i-ri-box-3-line',
                activeOn: ['kanban_offers'],
                to: accountScopedRoute('kanban_board', {}, { view: 'offers' }),
              },
              {
                name: 'Message Templates',
                label: 'Modelos de mensagens',
                icon: h(
                  'svg',
                  {
                    xmlns: 'http://www.w3.org/2000/svg',
                    width: '16',
                    height: '16',
                    viewBox: '0 0 24 24',
                    fill: 'none',
                    stroke: 'currentColor',
                    'stroke-width': '2',
                    'stroke-linecap': 'round',
                    'stroke-linejoin': 'round',
                    class:
                      'lucide lucide-message-circle-more-icon lucide-message-circle-more',
                  },
                  [
                    h('path', {
                      d: 'M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719',
                    }),
                    h('path', { d: 'M8 12h.01' }),
                    h('path', { d: 'M12 12h.01' }),
                    h('path', { d: 'M16 12h.01' }),
                  ]
                ),
                activeOn: ['kanban_templates'],
                to: accountScopedRoute(
                  'kanban_board',
                  {},
                  { view: 'templates' }
                ),
              },
            ]
          : []),
        {
          name: 'Funnels',
          label: 'Funis',
          icon: 'i-ri-flow-chart',
          activeOn: ['kanban_board'],
          children:
            funnels.value?.map(funnel => ({
              name: `funnel-${funnel.id}`,
              label: funnel.name,
              to: accountScopedRoute('kanban_board', {}, { funnel: funnel.id }),
            })) || [],
        },
        ...(selectedFunnel.value
          ? [
              {
                name: 'Recent Items',
                label: 'Itens Recentes',
                icon: 'i-ri-time-line',
                activeOn: ['kanban_board'],
                children:
                  recentKanbanItems.value?.map(item => ({
                    name: `item-${item.id}`,
                    label: item.item_details?.title || `Item ${item.id}`,
                    to: accountScopedRoute(
                      'kanban_board',
                      {},
                      { item: item.id }
                    ),
                  })) || [],
              },
            ]
          : []),
      ],
    },
    {
      name: 'Scheduled Messages',
      label: t('SCHEDULE_MESSAGES.LIST.TITLE'),
      icon: 'i-ri-time-line',
      featureFlag: 'kanban_board',
      activeOn: ['kanban_board'],
      to: accountScopedRoute(
        'kanban_board',
        {},
        { view: 'scheduled_messages' }
      ),
    },
    {
      name: 'Flow automations',
      label: t('SIDEBAR.FLOW_AUTOMATIONS'),
      icon: 'i-ri-git-branch-line',
      featureFlag: 'kanban_board',
      activeOn: [
        'conversation_workflows_index',
        'conversation_workflows_edit',
        'conversation_workflows_executions',
        'conversation_workflows_execution_detail',
      ],
      to: accountScopedRoute('conversation_workflows_index'),
    },
    {
      name: 'Automation Logs',
      label: t('AUTOMATION_EVENTS.SIDEBAR'),
      icon: 'i-ri-draft-line',
      activeOn: ['automation_logs'],
      to: accountScopedRoute('automation_logs'),
    },
    {
      name: 'Tasks',
      icon: 'i-ri-list-check',
      label: 'Tarefas',
      to: accountScopedRoute('tasks'),
      activeOn: ['tasks'],
      children: [
        {
          name: 'Tasks-today',
          label: 'Hoje',
          icon: 'i-ri-calendar-2-line',
          to: accountScopedRoute('tasks', {}, { preset: 'today' }),
          activeOn: ['tasks'],
        },
        {
          name: 'Tasks-overdue',
          label: 'Atrasadas',
          icon: 'i-ri-alarm-line',
          to: accountScopedRoute('tasks', {}, { preset: 'overdue' }),
          activeOn: ['tasks'],
        },
        {
          name: 'Tasks-mine',
          label: 'Minhas tarefas',
          icon: 'i-ri-user-line',
          to: accountScopedRoute('tasks', {}, { preset: 'mine' }),
          activeOn: ['tasks'],
        },
        {
          name: 'Tasks-done',
          label: 'Concluídas',
          icon: 'i-ri-checkbox-circle-line',
          to: accountScopedRoute('tasks', {}, { preset: 'done' }),
          activeOn: ['tasks'],
        },
        {
          name: 'Tasks-blocked',
          label: 'Bloqueadas',
          icon: 'i-ri-lock-line',
          to: accountScopedRoute('tasks', {}, { preset: 'blocked' }),
          activeOn: ['tasks'],
        },
      ],
    },
    {
      name: 'Permutas',
      label: 'Permutas',
      icon: 'i-ri-repeat-line',
      featureFlag: 'permutas',
      to: accountScopedRoute('permutas_index'),
      activeOn: ['permutas_index'],
    },
    {
      name: 'Captain',
      icon: 'i-woot-captain',
      label: t('SIDEBAR.CAPTAIN'),
      activeOn: ['captain_assistants_create_index'],
      children: [
        {
          name: 'FAQs',
          label: t('SIDEBAR.CAPTAIN_RESPONSES'),
          activeOn: [
            'captain_assistants_responses_index',
            'captain_assistants_responses_pending',
          ],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_responses_index',
          }),
        },
        {
          name: 'Documents',
          label: t('SIDEBAR.CAPTAIN_DOCUMENTS'),
          activeOn: ['captain_assistants_documents_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_documents_index',
          }),
        },
        {
          name: 'Scenarios',
          label: t('SIDEBAR.CAPTAIN_SCENARIOS'),
          activeOn: ['captain_assistants_scenarios_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_scenarios_index',
          }),
        },
        {
          name: 'Playground',
          label: t('SIDEBAR.CAPTAIN_PLAYGROUND'),
          activeOn: ['captain_assistants_playground_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_playground_index',
          }),
        },
        {
          name: 'Inboxes',
          label: t('SIDEBAR.CAPTAIN_INBOXES'),
          activeOn: ['captain_assistants_inboxes_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_inboxes_index',
          }),
        },
        {
          name: 'Schedules',
          label: t('SIDEBAR.CAPTAIN_SCHEDULES'),
          activeOn: ['captain_assistants_schedules_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_schedules_index',
          }),
        },
        {
          name: 'Tools',
          label: t('SIDEBAR.CAPTAIN_TOOLS'),
          activeOn: ['captain_tools_index'],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_tools_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.CAPTAIN_SETTINGS'),
          activeOn: [
            'captain_assistants_settings_index',
            'captain_assistants_guidelines_index',
            'captain_assistants_guardrails_index',
            'captain_assistants_materials_index',
            'captain_assistants_ai_panel_index',
            'captain_assistants_tool_selection_index',
            'captain_assistants_field_rules_index',
            'captain_assistants_routing_rules_index',
          ],
          to: accountScopedRoute('captain_assistants_index', {
            navigationPath: 'captain_assistants_settings_index',
          }),
        },
        {
          // SiteUp (unificação): o "Motor de IA" (provider LLM da conta,
          // AiProviderSetting) é compartilhado e agora vive sob o Captain.
          // Gating por 'ai_agent' vem da meta da rota ai_agents_provider (via
          // isAllowed) — não precisa featureFlag no item.
          name: 'AI Engine',
          label: 'Motor de IA',
          activeOn: ['ai_agents_provider'],
          to: accountScopedRoute('ai_agents_provider'),
        },
      ],
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-ri-contacts-line',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'WhatsApp Duplicates',
          label: t('SIDEBAR.WAHA_DUPLICATES'),
          to: accountScopedRoute('contacts_waha_duplicates'),
          activeOn: ['contacts_waha_duplicates'],
        },
        {
          name: 'Segments',
          icon: 'i-ri-group-line',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          collapsible: true,
          showTreeLine: true,
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-ri-price-tag-line',
          label: t('SIDEBAR.TAGGED_WITH'),
          collapsible: true,
          showTreeLine: true,
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    {
      name: 'Companies',
      label: t('SIDEBAR.COMPANIES'),
      icon: 'i-ri-building-2-line',
      children: [
        {
          name: 'All Companies',
          label: t('SIDEBAR.ALL_COMPANIES'),
          to: accountScopedRoute(
            'companies_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['companies_dashboard_index', 'companies_dashboard_show'],
        },
      ],
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-ri-line-chart-line',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports SLA',
          label: t('SIDEBAR.REPORTS_SLA'),
          to: accountScopedRoute('sla_reports'),
        },
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          to: accountScopedRoute('bot_reports'),
        },
      ],
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-ri-megaphone-line',
      children: [
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
        },
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
        {
          // Mockup 19 / G3 — lista unificada de agendados (não unifica motores).
          name: 'Scheduled',
          label: t('SIDEBAR.SCHEDULED'),
          to: accountScopedRoute('campaigns_scheduled_index'),
        },
      ],
    },
    {
      name: 'Email Sequences',
      label: 'Sequências Email',
      icon: 'i-ri-mail-line',
      featureFlag: 'email_sequences',
      to: accountScopedRoute('email_sequences'),
    },
    {
      name: 'WhatsApp Groups',
      label: 'Grupos WhatsApp',
      icon: 'i-ri-group-line',
      featureFlag: 'whatsapp_groups',
      activeOn: [
        'whatsapp_groups_index',
        'whatsapp_groups_dashboard',
        'whatsapp_groups_campaigns',
        'whatsapp_groups_campaign_detail',
        'whatsapp_groups_list',
        'whatsapp_group_detail',
        'whatsapp_groups_messages',
        'whatsapp_groups_sequences',
        'whatsapp_groups_webhooks',
        'whatsapp_groups_activities',
        'whatsapp_groups_launch_ops',
        'whatsapp_groups_launch_link',
      ],
      to: accountScopedRoute('whatsapp_groups_campaigns'),
    },
    {
      name: 'Comment to DM',
      label: 'Comentário IG → DM',
      icon: 'i-ri-instagram-line',
      activeOn: ['instagram_automation_index'],
      to: accountScopedRoute('instagram_automation_index'),
    },
    {
      name: 'FB Comment to DM',
      label: 'Comentário FB → DM',
      icon: 'i-ri-facebook-line',
      activeOn: ['facebook_automation_index'],
      to: accountScopedRoute('facebook_automation_index'),
    },
    {
      name: 'Voip Calls',
      label: t('SIDEBAR.VOIP_CALLS'),
      icon: 'i-lucide-phone-call',
      featureFlag: 'voip_calls',
      children: [
        {
          name: 'Voip Calls Dashboard',
          label: 'Visão geral',
          icon: 'i-ri-dashboard-line',
          to: accountScopedRoute('voip_calls'),
        },
        {
          name: 'Voip Leads',
          label: 'Leads',
          icon: 'i-ri-group-line',
          to: accountScopedRoute('voip_leads'),
        },
        {
          name: 'Voip Analytics',
          label: 'Desempenho',
          icon: 'i-ri-bar-chart-grouped-line',
          to: accountScopedRoute('voip_analytics'),
        },
        {
          name: 'Voip Calls Settings',
          label: 'Configurações',
          icon: 'i-ri-settings-line',
          to: accountScopedRoute('voip_calls_settings'),
        },
      ],
    },
    {
      name: 'Help Center',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-ri-book-shelf-line',
      to: accountScopedRoute('help_center_index'),
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-ri-flashlight-line',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-ri-briefcase-line',
          to: accountScopedRoute('general_settings_index'),
        },
        // {
        //   name: 'Settings Captain',
        //   label: t('SIDEBAR.CAPTAIN_AI'),
        //   icon: 'i-woot-captain',
        //   to: accountScopedRoute('captain_settings_index'),
        // },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-ri-account-box-line',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-ri-group-line',
          activeOn: [
            'settings_teams_list',
            'settings_teams_new',
            'settings_teams_finish',
            'settings_teams_add_agents',
            'settings_teams_show',
            'settings_teams_edit',
            'settings_teams_edit_members',
            'settings_teams_edit_finish',
          ],
          to: accountScopedRoute('settings_teams_list'),
        },
        ...(hasAdvancedAssignment.value
          ? [
              {
                name: 'Settings Agent Assignment',
                label: t('SIDEBAR.AGENT_ASSIGNMENT'),
                icon: 'i-ri-user-settings-line',
                activeOn: [
                  'assignment_policy_index',
                  'agent_assignment_policy_index',
                  'agent_assignment_policy_create',
                  'agent_assignment_policy_edit',
                  'agent_capacity_policy_index',
                  'agent_capacity_policy_create',
                  'agent_capacity_policy_edit',
                ],
                to: accountScopedRoute('assignment_policy_index'),
              },
            ]
          : []),
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-ri-inbox-line',
          activeOn: [
            'settings_inbox_list',
            'settings_inbox_show',
            'settings_inbox_new',
            'settings_inbox_finish',
            'settings_inboxes_page_channel',
            'settings_inboxes_add_agents',
          ],
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-ri-price-tag-2-line',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-ri-code-line',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-ri-repeat-line',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-ri-robot-line',
          to: accountScopedRoute('agent_bots'),
        },
        {
          // Lead Scoring — feature extraída do VOIP pra virar nativa.
          // Só aparece quando a flag lead_scoring está habilitada na conta.
          name: 'Settings Lead Scoring',
          label: 'Lead Scoring',
          icon: 'i-ri-flashlight-line',
          featureFlag: 'lead_scoring',
          to: accountScopedRoute('lead_scoring_settings'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-ri-apps-2-line',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-ri-chat-quote-line',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-ri-apps-2-line',
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-ri-briefcase-line',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-ri-shield-check-line',
          to: accountScopedRoute('custom_roles_list'),
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-ri-alarm-warning-line',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Conversation Workflow',
          label: t('SIDEBAR.CONVERSATION_WORKFLOW'),
          icon: 'i-ri-flow-chart',
          to: accountScopedRoute('conversation_workflow_index'),
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-ri-shield-line',
          to: accountScopedRoute('security_settings_index'),
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-ri-bank-card-line',
          to: accountScopedRoute('billing_settings_index'),
        },
      ],
    },
  ];

  // Add dashboard apps to the menu items
  const dashboardAppItems = dashboardApps.value.map(app => ({
    name: `app_${app.id}`,
    label: app.title,
    badgeLabel: 'App',
    // SiteUP V2 (F1.2e): `dashboard_apps.icon` guarda a classe completa. O
    // catálogo agora oferece Remix, mas app criado antes da migração continua
    // com `i-lucide-*` no banco (nada foi migrado). Aceita as duas famílias e
    // traduz o legado na leitura; só cai no fallback quando não é classe de
    // ícone (nulo, vazio ou lixo).
    icon: resolveSavedIconClass(
      typeof app.icon === 'string' && /^i-(ri|lucide)-/.test(app.icon)
        ? app.icon
        : 'i-ri-link'
    ),
    to: accountScopedRoute('dashboard_apps_show', { id: app.id }),
    toStateName: 'dashboard_apps_show',
    activeOn: ['dashboard_apps_show'],
  }));

  // SiteUp: oculta os itens de topo que o usuário escondeu em
  // Perfil > Menu lateral (uiSettings.sidebar_hidden_menu_items). Só esconde
  // itens de topo; cargo/permissão e feature flags continuam valendo via
  // SidebarGroup (isAllowed), então itens que o usuário nem pode ver nunca
  // aparecem — nem aqui, nem na lista de personalização.
  const hiddenItems = uiSettings.value?.sidebar_hidden_menu_items || [];
  // SiteUp: "Relatórios" some do flyout mobile (telas densas em tabelas/
  // gráficos não cabem bem em telas pequenas). Desktop continua intacto —
  // a rota/deep link em si não é removida, só o item de navegação mobile.
  const mobileOnlyHidden = isMobile.value ? ['Reports'] : [];
  return [...items, ...dashboardAppItems].filter(
    item =>
      !hiddenItems.includes(item.name) && !mobileOnlyHidden.includes(item.name)
  );
});
</script>

<template>
  <!-- SiteUp/F1.4 follow-up: onClickOutside (VueUse) escuta em pointerdown,
       que dispara ANTES do click do botão "Menu" da MobileBottomNav — sem
       o ignore de #mobile-bottom-nav, o pointerdown já fecha a sidebar e o
       click do próprio botão (toggleMobileSidebar, um flip simples) reabre
       em seguida, deixando o menu preso aberto sempre que o usuário tenta
       fechar tocando nele de novo. -->
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      {
        ignore: [
          '#mobile-sidebar-launcher',
          '#mobile-bottom-nav',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="group/sidebar bg-n-background flex flex-col text-sm pb-px fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-n-weak"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <section
      class="grid"
      :class="isEffectivelyCollapsed ? 'mt-3 mb-6 gap-4' : 'mt-1 mb-4 gap-2'"
    >
      <div
        class="flex gap-2 items-center min-w-0"
        :class="{
          'justify-center px-1': isEffectivelyCollapsed,
          'px-2': !isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <SidebarAccountSwitcher
            is-collapsed
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
        <template v-else>
          <div class="grid flex-shrink-0 place-content-center size-6">
            <Logo class="size-4" />
          </div>
          <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
          <SidebarAccountSwitcher
            class="flex-grow -mx-1 min-w-0"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
      </div>
      <div
        class="flex gap-2"
        :class="isEffectivelyCollapsed ? 'flex-col items-center' : 'px-2'"
      >
        <RouterLink
          v-if="!isEffectivelyCollapsed"
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-2 py-1 w-full h-7 rounded-xl outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out"
        >
          <span class="flex-shrink-0 i-ri-search-line size-4 text-n-slate-10" />
          <span class="flex-grow text-start text-n-slate-10">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <RouterLink
          v-else
          :to="{ name: 'search' }"
          class="flex items-center justify-center size-8 rounded-xl outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out hover:bg-n-alpha-2 dark:hover:bg-n-slate-9/30"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-ri-search-line size-4 text-n-slate-11" />
        </RouterLink>
        <ComposeConversation align="start">
          <template #trigger="{ isOpen }">
            <Button
              icon="i-ri-edit-2-line"
              color="slate"
              size="sm"
              class="dark:hover:!bg-n-slate-9/30"
              :class="[
                isEffectivelyCollapsed
                  ? '!size-8 !outline-n-weak !text-n-slate-11'
                  : '!h-7 !outline-n-weak !text-n-slate-11',
                { '!bg-n-alpha-2 dark:!bg-n-slate-9/30': isOpen },
              ]"
            />
          </template>
        </ComposeConversation>
      </div>
    </section>
    <nav
      class="grid overflow-y-scroll flex-grow gap-2 pb-5 no-scrollbar min-w-0"
      :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
    >
      <ul
        class="flex flex-col gap-1 m-0 list-none min-w-0"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
      />
      <div
        class="px-1 py-1.5 flex-shrink-0 flex w-full z-50 gap-2 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
        :class="
          isEffectivelyCollapsed ? 'flex-col justify-center' : 'justify-between'
        "
      >
        <SidebarTasksBell />
        <!-- SiteUp — Modo Foco: o mesmo botão liga e desliga -->
        <button
          type="button"
          class="flex items-center justify-center flex-shrink-0 size-8 rounded-full text-[#7856FF] transition-colors duration-150"
          :class="
            isFocusModeEnabled
              ? 'bg-[#7856FF]/25 hover:bg-[#7856FF]/35'
              : 'hover:bg-[#7856FF]/15'
          "
          :title="
            isFocusModeEnabled
              ? t('SIDEBAR.FOCUS_MODE.DEACTIVATE')
              : t('SIDEBAR.FOCUS_MODE.ACTIVATE')
          "
          :aria-label="
            isFocusModeEnabled
              ? t('SIDEBAR.FOCUS_MODE.DEACTIVATE')
              : t('SIDEBAR.FOCUS_MODE.ACTIVATE')
          "
          :aria-pressed="isFocusModeEnabled"
          @click="onFocusModeClick"
        >
          <span
            class="size-4"
            :class="
              isFocusModeEnabled
                ? 'i-ri-radio-button-fill'
                : 'i-ri-radio-button-line'
            "
          />
        </button>
        <SidebarProfileMenu
          :is-collapsed="isEffectivelyCollapsed"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </section>
    <!-- Resize Handle (desktop only) -->
    <div
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="toggleCollapse"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
    <!-- Collapse / Expand toggle (desktop only) -->
    <button
      type="button"
      class="hidden md:flex absolute z-50 top-1/2 -translate-y-1/2 ltr:-right-3 rtl:-left-3 items-center justify-center rounded-full border border-n-weak bg-n-background text-n-slate-11 size-6 opacity-0 group-hover/sidebar:opacity-100 focus-visible:opacity-100 transition-all duration-200 hover:bg-n-alpha-2 hover:text-n-slate-12 dark:hover:bg-n-slate-9/30"
      :title="
        isEffectivelyCollapsed ? t('SIDEBAR.EXPAND') : t('SIDEBAR.COLLAPSE')
      "
      :aria-label="
        isEffectivelyCollapsed ? t('SIDEBAR.EXPAND') : t('SIDEBAR.COLLAPSE')
      "
      @click="toggleCollapse"
    >
      <span
        class="size-3.5"
        :class="
          (isEffectivelyCollapsed ? !isRTL : isRTL)
            ? 'i-ri-arrow-right-double-line'
            : 'i-ri-arrow-left-double-line'
        "
      />
    </button>
  </aside>
</template>
